import { useEffect, useRef, useState } from "react";
import { 
  FaDatabase, 
  FaBrain, 
  FaMapMarkerAlt, 
  FaFileAlt, 
  FaHandPointer, 
  FaMagic, 
  FaCheckDouble, 
  FaCheckCircle,
  FaExclamationTriangle,
  FaChevronRight,
  FaCheck,
  FaGears
} from "react-icons/fa";
import Modal from "../../../utils/components/Modal";
import Button from "../../../utils/components/Button";
import toastr from "toastr";
import API from "../../../utils/API";

/** Text description is sent to `forms/auto-generate`; the stream returns events until the form is created. */

const COMPLETED = 'completed';

const STAGES = [
  { key: "retreiving_site_sets", label: "Loading project structure", icon: <FaDatabase /> },
  { key: "user_prompt_processing", label: "Analyzing instructions", icon: <FaBrain /> },
  { key: "matching_site_sets", label: "Identifying location", icon: <FaMapMarkerAlt /> },
  { key: "retreiving_form_templates", label: "Locating templates", icon: <FaFileAlt /> },
  { key: "selection_required", label: "Human selection", icon: <FaHandPointer /> },
  { key: "extracting_form_fields", label: "Extracting details", icon: <FaMagic /> },
  { key: "approve_form_details", label: "Final confirmation", icon: <FaCheckDouble /> },
  { key: "extracting_description_field", label: "Extracting description field", icon: <FaFileAlt /> },
  { key: "form_is_creating", label: "Generating form", icon: <FaMagic /> },
  { key: COMPLETED, label: "Completed", icon: <FaCheckCircle /> },
];

const SpeechProcessTimeline = ({ 
  events, 
  selectionData, 
  approvalData, 
  handleSelect, 
  handleApprove, 
  timeLeft, 
  formatTime, 
  translate,
  error
}) => {
  const scrollRef = useRef(null);
  const fillingEvents = events.filter((e) => e.type === "auto_form_filling");
  const lastEvent = fillingEvents[fillingEvents.length - 1];
  
  // If we have an error and no events yet, default to the first stage to show the error
  const currentStage = lastEvent?.stage || (error ? STAGES[0].key : null);
  const currentIndex = STAGES.findIndex((s) => s.key === currentStage);

  useEffect(() => {
    const scrollToBottom = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    };
    
    // Initial scroll
    scrollToBottom();
    
    // Brief delay to ensure any newly rendered elements (like selection cards) are accounted for
    const timer = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timer);
  }, [events, selectionData, approvalData, error]);

  const getDetail = (stageKey) => {
    const event = fillingEvents.find((e) => e.stage === stageKey);
    if (!event) return null;

    if (stageKey === "matching_site_sets") {
      const next = fillingEvents.find((e) => e.stage === "retreiving_form_templates");
      return next?.data?.match?.location;
    }
    if (stageKey === "retreiving_form_templates") {
      const next = fillingEvents.find((e) => e.stage === "selection_required");
      if (next) return `${next.data.form_template_options.length} options found`;
      const extract = fillingEvents.find((e) => e.stage === "extracting_form_fields");
      return extract?.data?.form_template_name;
    }
    if (stageKey === "extracting_form_fields") {
      return event.data?.form_template_name;
    }
    if (stageKey === "approve_form_details") {
      return event.data?.name;
    }
    if (stageKey === "completed") {
      return (
        <div className="d-flex align-items-center gap-2">
          <span className="small fw-bold text-orange">{translate("forms.created_successfully")}:</span>
          <a className="text-underline" href={`/forms/${event.data?.form_id}`}>{event.data?.form_name}</a>
        </div>
      )
    }
    return null;
  };

  if (fillingEvents.length === 0 && !error) return null;

  // Progressive disclosure: only show stages reached so far
  const visibleStages = STAGES.slice(0, currentIndex + 1);

  return (
    <div className="agentic-timeline-container" ref={scrollRef}>
      <div className="agentic-timeline">
        {visibleStages.map((stage, index) => {
          const isCompleted = index < currentIndex || (currentStage === COMPLETED && !error);
          const isActive = index === currentIndex && currentStage !== COMPLETED;
          const isFailed = isActive && error;
          const detail = getDetail(stage.key);

          return (
            <div
              key={stage.key}
              className={`timeline-item ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""} ${isFailed ? "failed" : ""}`}
            >
              <div className="timeline-icon">
                {isFailed ? <FaExclamationTriangle /> : stage.icon}
                {isActive && !isFailed && <div className="pulse-ring"></div>}
              </div>
              <div className="timeline-content">
                <div className="timeline-label">{stage.label}</div>
                {isActive && !isFailed && !isCompleted && <div className="pulse-text">Processing...</div>}
                {isFailed && <div className="text-danger x-small mt-1 fw-600">{error}</div>}
                {detail && <div className="timeline-detail">{detail}</div>}
              </div>
            </div>
          );
        })}

        {/* Global Interactive Area (Always at the bottom) */}
        {currentStage === "selection_required" && !error && selectionData && (
          <div className="agentic-interactive-card">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center gap-2">
                <div className="icon-pulse-small" style={{ backgroundColor: "#fa6400" }}></div>
                <div className="d-flex align-items-start gap-2 flex-column">
                  <span className="small fw-bold text-orange">{translate("select_template_to_continue")}</span>
                  <p className="text-muted mb-0 x-small"
                    dangerouslySetInnerHTML={{
                      __html: translate("automatic_form_location_found_hint").replace(
                        "{location}",
                        selectionData?.site_set_detail?.location
                      ),
                    }}
                  />
                </div>
              </div>
              <span className="badge font-monospace" style={{ backgroundColor: "#fa6400" }}>{formatTime(timeLeft)}</span>
            </div>
            <div className="list-group list-group-flush border rounded overflow-hidden shadow-sm">
              {selectionData.form_template_options.map((tmpl) => (
                <button
                  key={tmpl.id}
                  type="button"
                  className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2 small"
                  onClick={() => handleSelect(tmpl.id)}
                >
                  <span>{tmpl.name}</span>
                  <FaChevronRight className="text-muted small" />
                </button>
              ))}
            </div>
          </div>
        )}

        {currentStage === "approve_form_details" && !error && approvalData && (
          <div className="agentic-interactive-card mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="d-flex align-items-center gap-2">
                <div className="icon-pulse-small" style={{ backgroundColor: "#f79616" }}></div>
                <span className="small fw-bold text-orange">{translate("approve_extracted_details")}</span>
              </div>
              <span className="badge font-monospace" style={{ backgroundColor: "#f79616" }}>{formatTime(timeLeft)}</span>
            </div>
            <div className="p-2 rounded mb-2" style={{ background: "rgba(250, 100, 0, 0.04)", border: "1px solid rgba(250, 100, 0, 0.15)" }}>
              <div className="small fw-bold mb-1 text-uppercase" style={{ color: "#f79616" }}>{translate("form_name")}</div>
              <div className="x-small mb-3 fw-600 text-dark">{approvalData.name}</div>
              <div className="small fw-bold mb-1 text-uppercase" style={{ color: "#f79616" }}>{translate("form_description")}</div>
              <div className="x-small text-secondary" style={{ whiteSpace: "pre-wrap" }}>{approvalData.description}</div>
            </div>
            <div className="d-flex gap-2">
              <Button className="btn-orange text-white flex-fill" onClick={() => handleApprove(true)}>
                <FaCheck className="me-2" /> {translate("confirm_and_create")}
              </Button>
              <Button className="btn-outline-danger" onClick={() => handleApprove(false)}>
                {translate("retype")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AutomaticFormCreationModal = ({
  translate,
  show,
  setShow,
  project
}) => {
  const [uploading, setUploading] = useState(false);
  const [text, setText] = useState("");
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [selectionData, setSelectionData] = useState(null);
  const [approvalData, setApprovalData] = useState(null);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [timeoutAt, setTimeoutAt] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (!show) {
      setUploading(false);
      setText("");
      setEvents([]);
      setError(null);
      setSelectionData(null);
      setApprovalData(null);
      setActiveSessionId(null);
      setTimeoutAt(null);
      setTimeLeft(0);
    }
  }, [show]);

  useEffect(() => {

    const updateTimer = () => {
      const now = Math.floor(Date.now() / 1000);
      const remaining = Math.max(0, Math.floor(timeoutAt - now));
      setTimeLeft(remaining);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [timeoutAt]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const handleSelect = async (formTemplateId) => {
    const selectedFormTemplate = selectionData?.form_template_options.find((tmpl) => tmpl.id === formTemplateId)

    if (!selectedFormTemplate) return;

    try {
      await API.postData("forms/auto-generate/select", {
        session_id: activeSessionId,
        data: {
          form_template_id: selectedFormTemplate.id,
          form_template_name: selectedFormTemplate.name,
        }
      });
      // After selecting, the stream on the backend will resume.
      // We clear the selection UI to show the progress again.
      setSelectionData(null);
    } catch (err) {
      toastr.error(translate("selection_failed"));
    }
  };

  const handleApprove = async (confirmed) => {
    try {
      await API.postData("forms/auto-generate/select", {
        session_id: activeSessionId,
        data: {
          confirm: confirmed
        }
      });
      setApprovalData(null);
    } catch (err) {
      toastr.error(translate("approval_failed"));
    }
  };

  const submitText = async () => {
    if (!project?.id) {
      toastr.error(translate("select_a_project_alert"));
      return;
    }
    const payload = {
      text
    };

    setUploading(true);
    setError(null);
    setEvents([]);
    setSelectionData(null);
    setApprovalData(null);
    setActiveSessionId(null);
    setTimeoutAt(null);

    let processedLength = 0;

    try {
      await API.postDataStream(
        `forms/auto-generate?project_id=${project.id}`,
        payload,
        {
          onDownloadProgress: (progressEvent) => {
            const xhr = progressEvent.event?.target;
            if (!xhr || !xhr.responseText) return;

            const responseText = xhr.responseText;
            
            const lastNewline = responseText.lastIndexOf('\n');
            if (lastNewline >= processedLength) {
              const completeLines = responseText.substring(processedLength, lastNewline + 1);
              processedLength = lastNewline + 1;
              
              const parts = completeLines.split('\n');
              for (const line of parts) {
                const trimmed = line.trim();
                if (!trimmed) continue;
                try {
                  const parsed = JSON.parse(trimmed);
                  
                  if (parsed.type === "error") {
                    setError(parsed.message);
                    toastr.error(parsed.message);
                    return;
                  }

                  setEvents((prev) => [...prev, parsed]);

                  // If the backend requires a selection or approval, show the UI
                  if (parsed.type === "auto_form_filling") {
                    if (parsed.stage === "selection_required") {
                      setSelectionData(parsed.data);
                    } else if (parsed.stage === "approve_form_details") {
                      setApprovalData(parsed.data);
                    }
                    setActiveSessionId(parsed.data.session_id);
                    setTimeoutAt(parsed.data.timeout - 5);
                  }
                } catch {
                  setEvents((prev) => [...prev, { type: "raw", line: trimmed }]);
                }
              }
            }
          }
        }
      );
    } catch (err) {
      const msg = err?.message || String(err);
      setError(msg);
      toastr.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const disableInteractions = uploading;

  return (
    <Modal
      title={translate("forms.automatic_form_creation")}
      modalSize="md"
      show={show}
      setShow={setShow}
      disableButtons={disableInteractions}
    >
      <div className="mb-3">
        {!project?.id ? (
          <p className="text-warning mb-0">{translate("select_a_project_alert")}</p>
        ) : (
          <p className="text-muted small mb-3">
            {translate("automatic_form_description_hint")}
          </p>
        )}
        {error ? (
          <p className="text-danger small">{error}</p>
        ) : null}
      </div>

      <div className="mb-3">
        <textarea
          className="form-control"
          rows="4"
          placeholder={translate("automatic_form_description_placeholder")}
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={disableInteractions}
        ></textarea>
      </div>
      
      <div className="mt-3">
        <SpeechProcessTimeline 
          events={events} 
          selectionData={selectionData}
          approvalData={approvalData}
          handleSelect={handleSelect}
          handleApprove={handleApprove}
          timeLeft={timeLeft}
          formatTime={formatTime}
          translate={translate}
          error={error}
        />
        {!events.length && (
          <div className="x-small p-4 text-center rounded" style={{ border: "1px dashed rgba(250,100,0,0.25)", background: "rgba(250,100,0,0.03)", color: "rgba(250,100,0,0.6)" }}>
            {translate("automatic_form_progress_empty")}
          </div>
        )}
      </div>

      <div className="d-flex flex-wrap gap-2 align-items-center mt-1">
        <Button
          className="btn-orange ms-auto text-white"
          disabled={
            disableInteractions || !text.trim() || !project?.id
          }
          onClick={submitText}
        >
          {translate("automatic_form_submit")}
        </Button>
      </div>  
    </Modal>
  );
};

export default AutomaticFormCreationModal;